import type { GetPortfolioConfigGlobalResult } from '@findigs/api';
import { createContext, useContext, useMemo } from 'react';
import type { PropsWithChildren } from 'react';
import type { PortfolioContextEntry, PortfolioOption, UUID } from '~/types';
import type { PortfolioCompanyGroup } from './group-portfolios-by-company';
import { groupPortfoliosByCompany } from './group-portfolios-by-company';

type PortfolioContextProps = {
  portfolioId?: UUID | null;
  portfolioInfo?: PortfolioContextEntry;
  portfolioConfig?: GetPortfolioConfigGlobalResult | null;
  portfolioList: PortfolioOption[];
  limitedPortfoliosList: PortfolioOption[];
  companyGroups: PortfolioCompanyGroup[];
  isFlexiRun: boolean;
  isTwoPhaseTenantScreening: boolean;
  petVerificationEnabled: boolean;
};

type PortfolioContextProvidedProps = Pick<
  PortfolioContextProps,
  | 'portfolioId'
  | 'portfolioInfo'
  | 'portfolioConfig'
  | 'portfolioList'
  | 'limitedPortfoliosList'
> & {
  portfolios?: PortfolioContextEntry[];
};

const PortfolioContext = createContext<PortfolioContextProps | null>(null);

export function usePortfolioContext() {
  const context = useContext(PortfolioContext);
  if (!context) {
    throw new Error(
      'usePortfolioContext must be used within <PortfolioContextProvider />'
    );
  }
  return context;
}

export function PortfolioContextProvider({
  portfolioId,
  portfolioInfo,
  portfolioConfig,
  portfolioList,
  limitedPortfoliosList,
  portfolios = [],
  children,
}: PropsWithChildren<PortfolioContextProvidedProps>) {
  const isFlexiRun = useMemo(
    () => Boolean(portfolioConfig?.pm_custom.flexible_run_functionality),
    [portfolioConfig]
  );

  const isTwoPhaseTenantScreening = useMemo(
    () => Boolean(portfolioConfig?.pm_custom?.two_phase_tenant_screening),
    [portfolioConfig]
  );

  const petVerificationEnabled = useMemo(
    () =>
      Boolean(
        portfolioConfig?.applicant_custom?.pet_check_configuration_enabled
      ),
    [portfolioConfig]
  );

  const companyGroups = useMemo(
    () => groupPortfoliosByCompany(portfolios),
    [portfolios]
  );

  const value = {
    portfolioId,
    portfolioInfo,
    portfolioConfig,
    portfolioList,
    limitedPortfoliosList,
    companyGroups,
    isFlexiRun,
    isTwoPhaseTenantScreening,
    petVerificationEnabled,
  };

  return (
    <PortfolioContext.Provider value={value}>
      {children}
    </PortfolioContext.Provider>
  );
}
